import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { ChevronDown, ChevronRight } from "lucide-react";

const OUTCOME_COLORS = {
    accepted: "default",
    refined: "secondary",
    rejected_reprompt: "warning",
    rejected_rollback: "destructive",
    abandoned: "outline",
};

export default function OutcomeRow({ outcome: o }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border rounded-lg p-3">
            <button className="w-full flex items-start gap-3 text-left" onClick={() => setExpanded(!expanded)}>
                {expanded
                    ? <ChevronDown className="h-4 w-4 mt-0.5 shrink-0" />
                    : <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={OUTCOME_COLORS[o.outcome] || "outline"}>{o.outcome}</Badge>
                        {o.rating && <Badge variant="outline">Rating: {o.rating}</Badge>}
                        <span className="text-xs text-muted-foreground">
                            {o.iterationCount} {t("admin.intelligence.outcomes.iterations")}
                        </span>
                        {o.requestType && (
                            <Badge variant="secondary" className="text-xs">{o.requestType}</Badge>
                        )}
                        <span className="text-xs text-muted-foreground ml-auto">
                            {new Date(o.createdAt).toLocaleString()}
                        </span>
                    </div>
                    <p className="text-sm mt-1 truncate text-muted-foreground">{o.userRequest?.slice(0, 150)}</p>
                </div>
            </button>
            {expanded && <OutcomeDetail outcome={o} t={t} />}
        </div>
    );
}

function OutcomeDetail({ outcome: o, t }) {
    return (
        <div className="mt-3 ml-7 space-y-3 text-sm">
            <div className="bg-muted rounded p-3">
                <pre className="text-xs whitespace-pre-wrap break-words">{o.userRequest}</pre>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <Field label={t("admin.intelligence.outcomes.workflow")} value={o.workflowName || o.workflowId} />
                <Field label={t("admin.intelligence.outcomes.company")} value={o.companyName || o.companyId} />
                <Field label={t("admin.intelligence.outcomes.category")} value={o.appCategory} />
                <Field label={t("admin.intelligence.outcomes.requestType")} value={o.requestType} />
                <Field label={t("admin.intelligence.outcomes.iterationCount")} value={o.iterationCount} />
                <Field label={t("admin.intelligence.outcomes.ratingLabel")} value={o.rating ? `${o.rating}/5` : null} />
            </div>
            {o.preferenceSignal && (
                <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">
                        {t("admin.intelligence.outcomes.preferenceSignal")}
                    </h4>
                    <div className="bg-muted/50 rounded p-2 text-xs">{o.preferenceSignal}</div>
                </div>
            )}
            {o.generatedPattern && (
                <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">
                        {t("admin.intelligence.outcomes.generatedPattern")}
                    </h4>
                    <div className="bg-muted rounded p-2">
                        <pre className="text-xs whitespace-pre-wrap break-words">
                            {JSON.stringify(o.generatedPattern, null, 2)}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, value }) {
    if (!value) return null;
    return (
        <div className="bg-muted/50 rounded p-2">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="text-xs font-medium break-all">{value}</div>
        </div>
    );
}
